import React, { useState, useEffect } from 'react';
import apiClient from '../../../api/apiClient';
import { formatDate } from '../../../utils/dateUtils';
import { formatPrice } from '../../../utils/priceUtils';
import { toast } from 'react-toastify';
import { Card, Button, Row, Col, Table, Badge, Alert } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActiveSubscriptionCard } from '../../component/ActiveSubscriptionCard';
import './MySubscriptions.css';

const MySubscriptions = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeSub, setActiveSub] = useState(null);
    const [plans, setPlans] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vendor, setVendor] = useState(null);
    const [leadTiers, setLeadTiers] = useState([]);
    const [extraLeads] = useState(0);
    const [standaloneLeads, setStandaloneLeads] = useState(0);
    const [showLeadsModal, setShowLeadsModal] = useState(false);
    const [leadPrice] = useState(10); // Fallback

    useEffect(() => {
        fetchData();
        loadRazorpayScript();
    }, []);

    useEffect(() => {
        if (location.state?.openBuyLeads) {
            setShowLeadsModal(true);
            navigate(location.pathname, { replace: true, state: null });
        } else if (location.state?.scrollToPlans) {
            setTimeout(() => {
                const element = document.getElementById('available-plans-section');
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.pathname, location.state, navigate]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [subRes, plansRes] = await Promise.all([
                apiClient.get('/vendors/subscription/active'),
                apiClient.get('/vendors/subscription/plans')
            ]);
            setActiveSub(subRes.data.subscription);
            setPlans(plansRes.data.plans);

            const userInfo = JSON.parse(localStorage.getItem('userInfo'));
            const vId = userInfo?.id;

            if (vId) {
                const [statsRes, tiersRes] = await Promise.all([
                    apiClient.get(`/vendors/${vId}/details`),
                    apiClient.get('/vendors/subscription/lead-tiers')
                ]);
                setVendor(statsRes.data.user);
                setTransactions(
                    [...(statsRes.data.user.Transactions || [])].sort((a, b) => Number(b.id) - Number(a.id))
                );
                setLeadTiers(tiersRes.data.tiers || []);
            }
        } catch (error) {
            console.error('fetchData error:', error);
            toast.error("Failed to load subscription data");
        } finally {
            setLoading(false);
        }
    };

    const calculateTieredPrice = (count) => {
        if (!count || count <= 0) return 0;
        if (leadTiers.length === 0) return count * leadPrice;

        let pricePerLead = leadTiers[0].price_per_lead;
        for (const tier of leadTiers) {
            if (count >= tier.min_leads && (!tier.max_leads || count <= tier.max_leads)) {
                pricePerLead = tier.price_per_lead;
                break;
            }
        }
        return count * parseFloat(pricePerLead);
    };

    const loadRazorpayScript = () => {
        if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) return;
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
    };

    const handleSubscribe = async (planId, extraLeadsCount = 0) => {
        try {
            const res = await apiClient.post('/vendors/subscription/create-order', {
                planId,
                extraLeads: extraLeadsCount
            });
            const { order, subscription, subscription_id, isSubscription } = res.data;

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_placeholder',
                amount: order ? order.amount : Math.round((res.data.finalPrice || 0) * 100),
                currency: "INR",
                name: "MangoLoops",
                description: isSubscription ? "Recurring Subscription Payment" : "Subscription Payment",
                handler: async (response) => {
                    try {
                        await apiClient.post('/vendors/subscription/verify-payment', {
                            planId,
                            extraLeads: extraLeadsCount,
                            razorpayData: response
                        });
                        toast.success("Subscription activated successfully!");
                        fetchData();
                    } catch (err) {
                        toast.error(err.response?.data?.message || "Payment verification failed");
                    }
                },
                prefill: {
                    name: JSON.parse(localStorage.getItem('userInfo'))?.name,
                    email: JSON.parse(localStorage.getItem('userInfo'))?.email,
                },
                theme: { color: "#4f46e5" }
            };

            const subId = subscription_id || (subscription && subscription.id);
            if (subId) {
                options.subscription_id = subId;
            } else if (order && order.id) {
                options.order_id = order.id;
            }

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            toast.error("Failed to initiate payment");
        }
    };

    if (loading) return <div className="page-content text-center">Loading...</div>;

    return (
        <div className="">
            <div className="">
                {/* <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <h4 className="mb-sm-0">My Subscription</h4>
                        </div>
                    </div>
                </div> */}

                <ActiveSubscriptionCard
                    activeSub={activeSub}
                    vendor={vendor}
                    onBuyExtraLeads={() => setShowLeadsModal(true)}
                    onRenewSubscription={() => {
                        const element = document.getElementById('available-plans-section');
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                        }
                    }}
                />

                <h4 className="mb-4" id="available-plans-section">Available Plans</h4>
                <Row className="g-3 mb-4 subscription-plans-row">
                    {plans.map(plan => {
                        if (Number(plan.duration_days) < 30) {
                            return null;
                        }
                        const isActivePlan = Number(activeSub?.plan_id) === Number(plan.id);

                        return (
                            <Col xs={12} sm={6} lg={3} key={plan.id} className="subscription-plan-col">
                                <Card className={`mb-0 pricing-box text-center shadow-sm position-relative subscription-plan-card ${isActivePlan ? 'active-plan-card' : 'border-0'}`}>
                                    {isActivePlan && <Badge bg="primary" className="active-plan-badge">Active Plan</Badge>}
                                    <Card.Body className="p-3 pt-4 d-flex flex-column">
                                        <div>
                                            <h5 className="mb-1">{plan.name}</h5>
                                            <p className="text-muted">Best for growing businesses</p>
                                        </div>
                                        <div className="pricing-price mb-4">
                                            <h2 className="mb-0">₹{formatPrice(plan.price)} <span className="fs-14 text-muted">/ {plan.duration_days} Days</span></h2>
                                        </div>
                                        <ul className="list-unstyled vstack gap-2 text-start mb-4">
                                            <li>
                                                <i className="ri-checkbox-circle-fill text-success me-2 fs-16 align-middle"></i>
                                                {plan.leads_included} Included Leads
                                            </li>
                                            <li>
                                                <i className="ri-checkbox-circle-fill text-success me-2 fs-16 align-middle"></i>
                                                {plan.max_package_listings ? `${plan.max_package_listings} Max Package Listings` : 'Unlimited Package Listings'}
                                            </li>
                                            <li>
                                                <i className="ri-checkbox-circle-fill text-success me-2 fs-16 align-middle"></i>
                                                {plan.max_leads_per_listing ? `${plan.max_leads_per_listing} Leads Limit / Listing` : 'Unlimited Leads / Listing'}
                                            </li>
                                            {plan.features && JSON.parse(plan.features).map((feature, idx) => (
                                                <li key={idx}>
                                                    <i className="ri-checkbox-circle-fill text-success me-2 fs-16 align-middle"></i>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>

                                        {/* <div className="mb-4">
                                        <label className="form-label text-start d-block">Additional Lead Pricing</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="0"
                                                min="0"
                                                disabled={isActivePlan}
                                                onChange={(e) => setExtraLeads(parseInt(e.target.value) || 0)}
                                            />
                                            <span className="input-group-text">Leads</span>
                                        </div>
                                        {!isActivePlan && extraLeads > 0 && (
                                            <div className="text-start mt-2">
                                                <small className="text-muted">
                                                    Plan: ₹{formatPrice(plan.price)} + Extra: ₹{formatPrice(calculateTieredPrice(extraLeads))} =
                                                    <strong className="text-primary"> Total: ₹{formatPrice(parseFloat(plan.price) + calculateTieredPrice(extraLeads))}</strong>
                                                </small>
                                            </div>
                                        )}
                                    </div> */}

                                        <Button
                                            variant={isActivePlan ? "outline-primary" : "primary"}
                                            className="w-100 mt-auto"
                                            disabled={isActivePlan}
                                            onClick={() => handleSubscribe(plan.id, extraLeads)}
                                        >
                                            {isActivePlan ? "Current Plan" : (activeSub ? "Upgrade Now" : "Subscribe Now")}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>


                {/* Standalone Leads Modal */}
                {showLeadsModal && (
                    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg">
                                <div className="modal-header bg-primary text-white border-0 pb-3">
                                    <h5 className="modal-title text-white mb-0">Buy Additional Leads</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowLeadsModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="mb-4">
                                        <label className="form-label fw-bold">How many leads do you need?</label>
                                        <div className="input-group input-group-lg">
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Enter quantity"
                                                min="1"
                                                value={standaloneLeads}
                                                onChange={(e) => setStandaloneLeads(parseInt(e.target.value) || 0)}
                                            />
                                            <span className="input-group-text">Leads</span>
                                        </div>
                                    </div>

                                    {leadTiers.length > 0 && (
                                        <div className="mb-4">
                                            <label className="form-label text-muted small mb-2">Pricing Structure:</label>
                                            <div className="bg-light p-2 rounded">
                                                {leadTiers.map(tier => (
                                                    <div key={tier.id} className={`d-flex justify-content-between px-2 py-1 ${standaloneLeads >= tier.min_leads && (!tier.max_leads || standaloneLeads <= tier.max_leads) ? 'bg-white shadow-sm rounded fw-bold text-primary' : 'text-muted'}`}>
                                                        <span>{tier.min_leads}{tier.max_leads ? ` - ${tier.max_leads}` : '+'} Leads</span>
                                                        <span>₹{formatPrice(tier.price_per_lead)}/lead</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded">
                                        <span className="h5 mb-0">Total Payable:</span>
                                        <span className="h3 mb-0 text-primary">₹{calculateTieredPrice(standaloneLeads)}</span>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 p-4 pt-0">
                                    <Button variant="light" className="px-4" onClick={() => setShowLeadsModal(false)}>Cancel</Button>
                                    <Button
                                        variant="primary"
                                        className="px-4"
                                        disabled={standaloneLeads <= 0}
                                        onClick={() => {
                                            handleSubscribe(null, standaloneLeads);
                                            setShowLeadsModal(false);
                                        }}
                                    >
                                        Pay Now
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <h4 className="mt-4 mb-4">Transaction History</h4>
                <Card className="border-0 shadow-sm">
                    <Card.Body>
                        <div className="table-responsive">
                            <Table className="table align-middle table-nowrap mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Date</th>
                                        <th>Plan</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Payment Method</th>
                                        <th>Razorpay Payment ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.length > 0 ? transactions.map(tx => (
                                        <tr key={tx.id}>
                                            <td>{formatDate(tx.transaction_date)}</td>
                                            <td>{tx.plan_name}</td>
                                            <td>₹{tx.amount}</td>
                                            <td>
                                                <Badge bg={tx.payment_status === 'success' ? 'success' : 'danger'}>
                                                    {tx.payment_status.toUpperCase()}
                                                </Badge>
                                            </td>
                                            <td>{tx.payment_method}</td>
                                            <td>{tx.razorpay_payment_id || '—'}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="6" className="text-center">No transactions found</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
};

export default MySubscriptions;
